import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { fetchPackageRecommendations } from 'actions/OfferActions'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { formatInlineList } from 'lib/format/formatList'
import { getRecommendationKey } from 'lib/offer/getRecommendationKey'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import {
  getDurationPackageOptions,
  getPropertyRatesForSelectedFlightMode,
} from 'selectors/offerPage/offerPageSelectors'

import { serialiseOccupancyByCategory } from './useSuggestedPackageOptionFromApi'

export interface SuggestedPropertyRatePackageResult {
  packageId: string
  rateId: string
  rate: App.EnrichedPropertyRate
  option: App.OfferPackageOptionView
  message: string
}

interface PackageRecommendation {
  packageId: string
  reason: string
}

type SuggestedPropertyRateOfferPageState = OfferPageState & {
  offer: App.HotelOffer
}

export function canUseSuggestedPropertyRatePackageOption(
  offerPageState: OfferPageState,
): offerPageState is SuggestedPropertyRateOfferPageState {
  return isLpcHotelOffer(offerPageState.offer)
}

function getPackageIds(option: App.OfferPackageOptionView): Array<string> {
  const packageIds = [option.package.id]

  if (option.package.lePackageId) {
    packageIds.push(option.package.lePackageId)
  }

  return packageIds
}

function matchesPackageRate(
  rate: App.EnrichedPropertyRate,
  packageIds: Array<string>,
  optionRateId: string | undefined,
) {
  return rate.service.type === 'lpc' &&
    packageIds.includes(rate.service.packageId) &&
    (!optionRateId || rate.service.rateId === optionRateId)
}

export function getBestPropertyRatePackageRecommendation(
  rankedPackages: Array<PackageRecommendation> | undefined,
  propertyRates: Array<App.EnrichedPropertyRate>,
  packageOptions: Array<App.OfferPackageOptionView>,
): { recommendation: PackageRecommendation, option: App.OfferPackageOptionView, rate: App.EnrichedPropertyRate } | undefined {
  if (!rankedPackages?.length) return undefined

  for (const recommendation of rankedPackages) {
    const option = packageOptions.find(option =>
      option.capacityValid &&
      option.hotelAvailable &&
      (
        option.package.id === recommendation.packageId ||
        option.package.lePackageId === recommendation.packageId
      ),
    )

    if (!option) continue

    const packageIds = getPackageIds(option)
    const optionRateId = option.package.roomRate?.id
    const matchingRate = propertyRates.find(rate => matchesPackageRate(rate, packageIds, optionRateId))

    if (matchingRate) {
      return { recommendation, option, rate: matchingRate }
    }
  }

  return undefined
}

function useSuggestedPropertyRatePackageOptionFromApi(): SuggestedPropertyRatePackageResult | undefined {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const offerPageState = useContext(OfferPageStateContext)
  const { rooms, checkInDate, checkOutDate } = offerPageState
  const lpcOfferPageState = canUseSuggestedPropertyRatePackageOption(offerPageState) ? offerPageState : undefined
  const offer = lpcOfferPageState?.offer

  const maxInfantAge = offer?.property.maxInfantAge ?? 2
  const occupancyString = useMemo(
    () => serialiseOccupancyByCategory(rooms, maxInfantAge),
    [rooms, maxInfantAge],
  )

  const offerId = offer?.id
  const recommendationKey = getRecommendationKey({
    offerId,
    occupancy: occupancyString,
    checkIn: checkInDate,
    checkOut: checkOutDate,
  })

  useEffect(() => {
    if (!offerId) return
    dispatch(fetchPackageRecommendations({
      offerId,
      occupancy: occupancyString,
      checkIn: checkInDate,
      checkOut: checkOutDate,
    }))
  }, [dispatch, offerId, occupancyString, checkInDate, checkOutDate])

  const recommendations = useAppSelector(state => lpcOfferPageState ? state.offer.packageRecommendations?.[recommendationKey] : undefined)
  const propertyRates = useAppSelector(useCallback(
    state => lpcOfferPageState ? getPropertyRatesForSelectedFlightMode(lpcOfferPageState, state) : EmptyArray,
    [lpcOfferPageState],
  ))
  const packageOptions = useAppSelector(useCallback(
    state => lpcOfferPageState ? getDurationPackageOptions(lpcOfferPageState, state) : EmptyArray,
    [lpcOfferPageState],
  ))

  return useMemo(() => {
    if (!lpcOfferPageState) return undefined

    const rankedPackages = recommendations?.data
    const bestRecommendation = getBestPropertyRatePackageRecommendation(rankedPackages, propertyRates, packageOptions)
    if (!bestRecommendation) return undefined

    const occupantsMessage = formatInlineList(formatOccupantsShort(rooms).split(', '), 'conjunction', { style: 'long' })
    const { recommendation, option, rate } = bestRecommendation

    if (recommendation.reason === 'BOOKINGS_BY_OCCUPANCY' && occupantsMessage) {
      return {
        packageId: recommendation.packageId,
        rateId: rate.rateId,
        rate,
        option,
        message: intl.formatMessage(
          {
            id: 'M2QxWVx3G5Xx-fHmKWhh',
            defaultMessage: 'Popular for {occupancy}',
            description: 'Indicates that a package is recommended for the selected room occupancy (e.g. "2 adults and 1 child")',
          },
          { occupancy: occupantsMessage },
        ),
      }
    }

    if (recommendation.reason === 'BOOKINGS_OVERALL') {
      return {
        packageId: recommendation.packageId,
        rateId: rate.rateId,
        rate,
        option,
        message: intl.formatMessage({
          id: 'IrcNoG-CK1--BRZBTOe7',
          defaultMessage: 'Popular',
          description: 'Indicates that a package is recommended due to overall popularity',
        }),
      }
    }

    return {
      packageId: recommendation.packageId,
      rateId: rate.rateId,
      rate,
      option,
      message: intl.formatMessage({
        id: 'PgFLEsD215DdFL4P95Id',
        defaultMessage: 'Recommended',
        description: 'Indicates that a package is recommended (no reason specified)',
      }),
    }
  }, [lpcOfferPageState, recommendations, propertyRates, packageOptions, rooms, intl])
}

export default useSuggestedPropertyRatePackageOptionFromApi
